import { useState, useEffect, useMemo, useCallback } from "react";
import { Check, X, Loader2, Search, ChevronLeft, Users, RefreshCw } from "lucide-react";

/**
 * Componente para pasar asistencia en un evento (estilos aqua/Montserrat).
 *
 * Flujo:
 *  1. Lee los inscriptos desde el Apps Script de la planilla (GET).
 *  2. Agrupa por "Centro Educativo ID". Se elige un centro y se ven sus estudiantes.
 *  3. Al marcar "Vino", el componente:
 *       a) le pega al PROXY (con tfa_1 = documento, tfa_3 = ID_AE). Si falla, no cuenta.
 *       b) si el proxy anduvo, marca "Vino" y además intenta escribir en la
 *          planilla vía Apps Script. Si esta segunda falla, la asistencia igual cuenta.
 *  4. La marca es definitiva (sin volver atrás).
 *
 * Config: completá las URLs de abajo.
 */
const WEB_APP_URL =
  "https://script.google.com/macros/s/AKfycbx4nBkGbNGoW0qrLBQDExLZFSBSrfyt6NIAGOROhIvYzKeWTv6VYgSXEgK1zML4yyPH/exec"; // ← URL /exec del Apps Script (leer + escribir planilla)
const PROXY_URL = "https://pideapi2025.azurewebsites.net/api/FormAssemblyProxy/submit"; // ← base del proxy (sin barra final)
const FORM_ID = "5228366"; // ← form ID numérico (va en la ruta del proxy)
const ID_AE = "a14Pn00000EvsJlIAJ"; // ← idAE fijo del evento (tfa_3)

// Nombres de columnas (deben coincidir con los encabezados de la planilla)
const COL = {
  nombre: "Nombre",
  apellidos: "Apellidos",
  documento: "Documento1",
  accion: "Acción educativa: Nombre de la acción educativa",
  rol: "Rol",
  centroId: "Centro Educativo ID",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap');
  .ej-app{
    --aqua:#00C0CA; --aqua-dark:#008d95; --sky:#99D9DF; --ice:#C2EDF0;
    --pearl:#EEF3F4; --lime:#8FC440; --ink:#22404D; --muted:#5d7679;
    --line:#cfe4e6; --card:#fff; --shadow:0 3px 16px rgba(0,141,149,.10);
    font-family:"Montserrat",system-ui,sans-serif; color:var(--ink);
    background:#fff; min-height:100vh; padding:24px 16px 80px; line-height:1.5;
  }
  .ej-app *{box-sizing:border-box;}
  .ej-app .wrap{max-width:640px;margin:0 auto;}

  .ej-app header.top{margin-bottom:22px;}
  .ej-app header.top .eyebrow{
    font-size:.72rem;letter-spacing:.24em;text-transform:uppercase;
    color:var(--aqua-dark);font-weight:800;
  }
  .ej-app header.top h1{
    font-weight:900;font-size:1.9rem;margin:.1em 0;letter-spacing:-.02em;
  }
  .ej-app header.top p{color:var(--muted);margin:0;font-size:.92rem;}

  .ej-app .search{position:relative;margin-bottom:18px;}
  .ej-app .search svg{
    position:absolute;left:14px;top:50%;transform:translateY(-50%);color:var(--aqua-dark);
  }
  .ej-app .search input{
    width:100%;padding:13px 14px 13px 42px;border:1.5px solid var(--line);
    border-radius:10px;background:var(--card);font-size:1rem;font-family:inherit;color:var(--ink);
  }
  .ej-app .search input:focus{outline:2px solid var(--aqua);border-color:var(--aqua);}

  .ej-app .back{
    display:inline-flex;align-items:center;gap:5px;background:none;border:none;
    color:var(--muted);font-family:inherit;font-size:.9rem;font-weight:700;
    cursor:pointer;padding:0;margin-bottom:16px;
  }
  .ej-app .back:hover{color:var(--aqua-dark);}

  .ej-app .centro-btn{
    width:100%;display:flex;align-items:center;justify-content:space-between;
    background:var(--card);border:1px solid var(--line);border-radius:12px;
    padding:16px 18px;margin-bottom:12px;cursor:pointer;text-align:left;
    box-shadow:var(--shadow);border-left:5px solid var(--aqua);transition:all .12s ease;
    font-family:inherit;color:var(--ink);
  }
  .ej-app .centro-btn:hover{border-left-color:var(--lime);transform:translateX(2px);}
  .ej-app .centro-btn .name{font-weight:800;font-size:1.05rem;}
  .ej-app .centro-btn .count{
    display:inline-flex;align-items:center;gap:6px;color:var(--aqua-dark);
    font-weight:700;font-size:.9rem;background:var(--ice);padding:4px 12px;border-radius:999px;
  }

  .ej-app .sub{
    display:inline-block;background:var(--ice);color:#0c4b4f;border-radius:8px;
    padding:6px 14px;font-size:.82rem;font-weight:700;margin-bottom:18px;
  }

  .ej-app .group-label{
    font-size:.7rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;
    color:var(--aqua-dark);margin:22px 0 10px;padding-left:2px;
  }

  .ej-app .row{
    display:flex;align-items:center;gap:12px;background:var(--card);
    border:1px solid var(--line);border-radius:12px;padding:14px 16px;margin-bottom:10px;
    box-shadow:var(--shadow);
  }
  .ej-app .row .info{min-width:0;flex:1;}
  .ej-app .row .info .n{font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ej-app .row .info .meta{
    font-size:.78rem;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px;
  }
  .ej-app .row .info .err{font-size:.76rem;color:#c0392b;margin-top:3px;}

  .ej-app .mark-btn{
    display:inline-flex;align-items:center;gap:6px;padding:10px 16px;border:none;
    border-radius:9px;font-family:inherit;font-size:.9rem;font-weight:800;cursor:pointer;
    background:var(--aqua);color:#fff;transition:background .12s ease;flex:0 0 auto;
  }
  .ej-app .mark-btn:hover{background:var(--aqua-dark);}
  .ej-app .mark-btn:disabled{opacity:.65;cursor:default;}
  .ej-app .mark-btn.err{background:#fdecea;color:#c0392b;}
  .ej-app .mark-btn.err:hover{background:#fbdcd7;}
  .ej-app .vino-badge{
    display:inline-flex;align-items:center;gap:6px;padding:10px 16px;border-radius:9px;
    background:var(--lime);color:#fff;font-weight:800;font-size:.9rem;flex:0 0 auto;
  }
  .ej-app .empty{text-align:center;color:var(--muted);font-size:.9rem;padding:36px 20px;}

  .ej-app .state{
    min-height:70vh;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:14px;text-align:center;color:var(--muted);
  }
  .ej-app .state .msg{font-weight:700;color:var(--ink);}
  .ej-app .retry{
    display:inline-flex;align-items:center;gap:8px;background:var(--aqua);color:#fff;border:none;
    border-radius:10px;padding:11px 20px;font-family:inherit;font-weight:800;cursor:pointer;
  }
  .ej-app .retry:hover{background:var(--aqua-dark);}
`;

function FilaEstudiante({ estudiante, mark, onMarcar, mostrarCentro }) {
  const e = estudiante;
  return (
    <div className="row">
      <div className="info">
        <div className="n">
          {e[COL.apellidos]}, {e[COL.nombre]}
        </div>
        <div className="meta">
          {e[COL.documento]}
          {e[COL.rol] ? ` · ${e[COL.rol]}` : ""}
          {mostrarCentro
            ? ` · ${e[COL.centroId] ? `Centro ${e[COL.centroId]}` : "Sin centro"}`
            : ""}
        </div>
        {mark.status === "error" && <div className="err">{mark.message}</div>}
      </div>

      {mark.status === "done" ? (
        <span className="vino-badge">
          <Check size={16} /> Vino
        </span>
      ) : (
        <button
          className={`mark-btn${mark.status === "error" ? " err" : ""}`}
          onClick={() => onMarcar(e)}
          disabled={mark.status === "calling"}
        >
          {mark.status === "calling" ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Enviando
            </>
          ) : mark.status === "error" ? (
            <>
              <X size={16} /> Reintentar
            </>
          ) : (
            "Marcar Vino"
          )}
        </button>
      )}
    </div>
  );
}

export default function AsistenciaEvento({
  webAppUrl = WEB_APP_URL,
  proxyUrl = PROXY_URL,
  formId = FORM_ID,
  idAE = ID_AE,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [selectedCentro, setSelectedCentro] = useState(null);
  const [query, setQuery] = useState("");
  const [globalQuery, setGlobalQuery] = useState("");

  // estado por documento: { status: 'idle'|'calling'|'done'|'error', message }
  const [marks, setMarks] = useState({});

  // ---------- carga de inscriptos desde el Apps Script (GET) ----------
  const loadSheet = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(webAppUrl);
      if (!res.ok) throw new Error(`No se pudo leer la planilla (${res.status})`);
      const payload = await res.json();
      if (!payload?.success) {
        throw new Error(payload?.message || "Respuesta inválida del script.");
      }
      const data = (payload.rows || [])
        .map((r) => {
          const obj = {};
          for (const k of Object.keys(r)) obj[k] = String(r[k] ?? "").trim();
          return obj;
        })
        .filter((r) => r[COL.documento]);
      setRows(data);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [webAppUrl]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  const centros = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const id = r[COL.centroId] || "Sin centro";
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(r);
    }
    return Array.from(map.entries())
      .map(([id, estudiantes]) => ({ id, estudiantes }))
      .sort((a, b) => a.id.localeCompare(b.id, "es", { numeric: true }));
  }, [rows]);

  const resultadosGlobales = useMemo(() => {
    const q = globalQuery.trim().toLowerCase();
    if (!q) return [];
    return rows
      .filter((e) =>
        `${e[COL.nombre]} ${e[COL.apellidos]} ${e[COL.documento]}`.toLowerCase().includes(q)
      )
      .sort((a, b) =>
        `${a[COL.apellidos]} ${a[COL.nombre]}`.localeCompare(`${b[COL.apellidos]} ${b[COL.nombre]}`, "es")
      );
  }, [rows, globalQuery]);

  const estudiantesCentro = useMemo(() => {
    if (!selectedCentro) return [];
    const centro = centros.find((c) => c.id === selectedCentro);
    if (!centro) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? centro.estudiantes.filter((e) =>
          `${e[COL.nombre]} ${e[COL.apellidos]} ${e[COL.documento]}`.toLowerCase().includes(q)
        )
      : centro.estudiantes;
    return [...list].sort((a, b) =>
      `${a[COL.apellidos]} ${a[COL.nombre]}`.localeCompare(`${b[COL.apellidos]} ${b[COL.nombre]}`, "es")
    );
  }, [selectedCentro, centros, query]);

  // dentro de un centro, al buscar, también mostramos coincidencias
  // entre los inscriptos sin centro asignado
  const sinCentroCoincidencias = useMemo(() => {
    if (!selectedCentro) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return rows
      .filter((e) => !(e[COL.centroId] || "").trim())
      .filter((e) =>
        `${e[COL.nombre]} ${e[COL.apellidos]} ${e[COL.documento]}`.toLowerCase().includes(q)
      )
      .sort((a, b) =>
        `${a[COL.apellidos]} ${a[COL.nombre]}`.localeCompare(`${b[COL.apellidos]} ${b[COL.nombre]}`, "es")
      );
  }, [selectedCentro, rows, query]);

  // ---------- marcar asistencia ----------
  // 1) pega al proxy (obligatorio: si falla, no cuenta como presente)
  // 2) si el proxy anduvo, escribe en la planilla vía Apps Script (best-effort)
  const marcarVino = useCallback(
    async (estudiante) => {
      const doc = estudiante[COL.documento];
      const current = marks[doc]?.status;
      if (current === "calling" || current === "done") return; // definitivo

      setMarks((m) => ({ ...m, [doc]: { status: "calling", message: "" } }));

      try {
        if (!idAE) throw new Error("Falta el ID AE del evento.");
        if (!formId || !/^\d+$/.test(formId))
          throw new Error(`Form ID inválido ("${formId}").`);

        // --- (1) PROXY: form-urlencoded, tal como espera ReadFormAsync ---
        const body = new URLSearchParams();
        body.set("tfa_1", doc); // Cédula
        body.set("tfa_3", idAE); // ID AE fijo

        const url = `${proxyUrl.replace(/\/$/, "")}/${encodeURIComponent(formId)}`;

        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });

        let payload = null;
        try {
          payload = await res.json();
        } catch (_) {
          /* respuesta no-JSON */
        }

        if (!res.ok || !payload?.success) {
          throw new Error(payload?.message || `El proxy respondió ${res.status}`);
        }

        // el proxy anduvo → cuenta como presente
        setMarks((m) => ({
          ...m,
          [doc]: { status: "done", message: payload.message || "Asistencia registrada" },
        }));

        // --- (2) PLANILLA (best-effort): no bloquea ni revierte la marca ---
        // text/plain para evitar el preflight CORS de Apps Script.
        fetch(webAppUrl, {
          method: "POST",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({ documento: doc }),
        }).catch(() => {
          /* si falla la escritura en planilla, la asistencia igual cuenta */
        });
      } catch (err) {
        setMarks((m) => ({ ...m, [doc]: { status: "error", message: err.message } }));
      }
    },
    [marks, idAE, formId, proxyUrl, webAppUrl]
  );

  const centroActual = centros.find((c) => c.id === selectedCentro);
  const presentesCount = estudiantesCentro.filter(
    (e) => marks[e[COL.documento]]?.status === "done"
  ).length;

  const nombreCentro = (id) => (id === "Sin centro" ? "Sin centro" : `Centro ${id}`);

  if (loading) {
    return (
      <div className="ej-app">
        <style>{CSS}</style>
        <div className="state">
          <Loader2 className="animate-spin" size={26} />
          <span>Cargando inscriptos…</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="ej-app">
        <style>{CSS}</style>
        <div className="state">
          <span className="msg">No se pudieron cargar los datos</span>
          <span>{loadError}</span>
          <button className="retry" onClick={loadSheet}>
            <RefreshCw size={16} /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ej-app">
      <style>{CSS}</style>
      <div className="wrap">
        {!selectedCentro ? (
          <div>
            <header className="top">
              <div className="eyebrow">Evento</div>
              <h1>Asistencia</h1>
              <p>Elegí un centro, o buscá directamente a cualquier inscripto.</p>
            </header>

            <div className="search">
              <Search size={16} />
              <input
                value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
                placeholder="Buscar en todos los inscriptos (incluye sin centro)"
              />
            </div>

            {globalQuery.trim() ? (
              <div>
                {resultadosGlobales.map((e) => (
                  <FilaEstudiante
                    key={e[COL.documento]}
                    estudiante={e}
                    mark={marks[e[COL.documento]] || { status: "idle" }}
                    onMarcar={marcarVino}
                    mostrarCentro
                  />
                ))}
                {resultadosGlobales.length === 0 && (
                  <p className="empty">Nadie coincide con la búsqueda.</p>
                )}
              </div>
            ) : (
              <div>
                {centros.map((c) => (
                  <button
                    key={c.id}
                    className="centro-btn"
                    onClick={() => {
                      setSelectedCentro(c.id);
                      setQuery("");
                    }}
                  >
                    <span className="name">{nombreCentro(c.id)}</span>
                    <span className="count">
                      <Users size={14} />
                      {c.estudiantes.length}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <button className="back" onClick={() => setSelectedCentro(null)}>
              <ChevronLeft size={16} /> Centros
            </button>

            <header className="top">
              <h1>{nombreCentro(centroActual?.id)}</h1>
            </header>

            <div className="sub">
              {presentesCount} de {centroActual?.estudiantes.length} presentes
            </div>

            <div className="search">
              <Search size={16} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre o documento"
              />
            </div>

            <div>
              {estudiantesCentro.map((e) => (
                <FilaEstudiante
                  key={e[COL.documento]}
                  estudiante={e}
                  mark={marks[e[COL.documento]] || { status: "idle" }}
                  onMarcar={marcarVino}
                />
              ))}

              {sinCentroCoincidencias.length > 0 && (
                <>
                  <div className="group-label">Sin centro asignado</div>
                  {sinCentroCoincidencias.map((e) => (
                    <FilaEstudiante
                      key={e[COL.documento]}
                      estudiante={e}
                      mark={marks[e[COL.documento]] || { status: "idle" }}
                      onMarcar={marcarVino}
                    />
                  ))}
                </>
              )}

              {estudiantesCentro.length === 0 && sinCentroCoincidencias.length === 0 && (
                <p className="empty">Nadie coincide con la búsqueda.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}